/**
 * PATH: apps/qa/src/components/OfflineStatus.tsx
 * PURPOSE: Display offline/online status and sync progress to users
 *
 * WHY:
 * - Field workers need to know when they're offline
 * - Show pending sync items count
 * - Provide manual sync trigger
 * - Show sync progress and errors
 *
 * FEATURES:
 * - Floating banner when offline
 * - Sync progress indicator
 * - Manual sync button
 * - Error notifications
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Wifi,
  WifiOff,
  RefreshCw,
  Cloud,
  CloudOff,
  Check,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Upload,
  Database,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import syncService, { useSyncStatus, SyncStatus } from '@/lib/sync-service'
import offlineDb from '@/lib/offline-db'

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function OfflineStatus() {
  const status = useSyncStatus()
  const [expanded, setExpanded] = useState(false)
  const [storageStats, setStorageStats] = useState<{
    inspections: number
    areas: number
    evidence: number
    pendingOperations: number
    failedOperations: number
  } | null>(null)

  // Load storage stats when expanded
  useEffect(() => {
    if (expanded) {
      offlineDb.getStorageStats().then(setStorageStats)
    }
  }, [expanded, status.pendingCount])

  const handleManualSync = useCallback(async () => {
    try {
      await syncService.forceSyncNow()
    } catch (error) {
      if (import.meta.env.DEV) console.error('Manual sync failed:', error)
    }
  }, [])

  // Don't show anything if online with no pending items
  const hasPending = status.pendingCount > 0 || status.failedCount > 0 || status.uploadingEvidence > 0
  const shouldShow = !status.isOnline || hasPending || status.isSyncing

  if (!shouldShow) {
    return null
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-40">
      {/* Main Banner */}
      <div
        className={cn(
          'rounded-xl shadow-lg border backdrop-blur-sm transition-all',
          status.isOnline
            ? 'bg-card/95 border-border'
            : 'bg-amber-50/95 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
        )}
      >
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-3 gap-3"
        >
          <div className="flex items-center gap-3">
            {/* Status Icon */}
            {status.isOnline ? (
              status.isSyncing ? (
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                </div>
              ) : status.failedCount > 0 ? (
                <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              ) : (
                <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Cloud className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
              )
            ) : (
              <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <WifiOff className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            )}

            {/* Status Text */}
            <div className="text-left">
              <p className={cn(
                'text-sm font-medium',
                status.isOnline ? 'text-foreground' : 'text-amber-800 dark:text-amber-200'
              )}>
                {status.isOnline
                  ? status.isSyncing
                    ? 'Syncing...'
                    : status.failedCount > 0
                      ? 'Sync Issues'
                      : status.pendingCount > 0
                        ? 'Pending Changes'
                        : 'Online'
                  : 'Offline Mode'}
              </p>
              <p className={cn(
                'text-xs',
                status.isOnline ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-300'
              )}>
                {status.pendingCount > 0 && `${status.pendingCount} pending`}
                {status.failedCount > 0 && `${status.pendingCount > 0 ? ', ' : ''}${status.failedCount} failed`}
                {status.uploadingEvidence > 0 && ` • ${status.uploadingEvidence} uploading`}
                {!hasPending && (status.lastSyncTime ? `Last sync: ${formatRelativeTime(status.lastSyncTime)}` : 'Ready')}
              </p>
            </div>
          </div>

          {/* Expand/Collapse */}
          <div className="flex items-center gap-2">
            {status.isOnline && !status.isSyncing && hasPending && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleManualSync()
                }}
                className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                title="Sync now"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </button>

        {/* Expanded Details */}
        {expanded && (
          <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
            {/* Connection Status */}
            <div className="flex items-center gap-2 text-sm">
              {status.isOnline ? (
                <>
                  <Wifi className="w-4 h-4 text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-600 dark:text-amber-400">No Internet</span>
                </>
              )}
            </div>

            {/* Storage Stats */}
            {storageStats && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-muted/50">
                  <div className="text-lg font-semibold text-foreground">
                    {storageStats.inspections}
                  </div>
                  <div className="text-xs text-muted-foreground">Cached</div>
                </div>
                <div className="p-2 rounded-lg bg-muted/50">
                  <div className="text-lg font-semibold text-foreground">
                    {storageStats.evidence}
                  </div>
                  <div className="text-xs text-muted-foreground">Photos</div>
                </div>
                <div className="p-2 rounded-lg bg-muted/50">
                  <div className="text-lg font-semibold text-foreground">
                    {storageStats.pendingOperations}
                  </div>
                  <div className="text-xs text-muted-foreground">Pending</div>
                </div>
              </div>
            )}

            {/* Sync Progress */}
            {status.isSyncing && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Syncing changes...</span>
                  <Loader2 className="w-3 h-3 animate-spin" />
                </div>
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full animate-pulse w-2/3" />
                </div>
              </div>
            )}

            {/* Failed Operations Warning */}
            {status.failedCount > 0 && (
              <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{status.failedCount} operation(s) failed to sync</span>
                </div>
                <button
                  onClick={handleManualSync}
                  className="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline"
                >
                  Retry sync
                </button>
              </div>
            )}

            {/* Offline Instructions */}
            <div className="p-2 rounded-lg bg-muted/50 text-xs text-muted-foreground">
              Offline changes sync when you reconnect. Queued work is bound to
              your user and organisation and clears on logout.
            </div>
            {!status.isOnline && (
              <div className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                <p>Your changes are saved on this device and sync when you reconnect.</p>
                <ul className="list-disc list-inside space-y-0.5 text-amber-600 dark:text-amber-400">
                  <li>You can continue inspections</li>
                  <li>Photos and checklist updates queue for your session only</li>
                  <li>Queues clear on logout so the next user cannot flush them</li>
                </ul>
              </div>
            )}

            {/* Last Sync Time */}
            {status.lastSyncTime && (
              <div className="text-xs text-muted-foreground text-center">
                Last successful sync: {formatRelativeTime(status.lastSyncTime)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Compact Indicator (for header/navbar)
// Exported as both OfflineIndicator and OfflineStatusCompact for compatibility
// ─────────────────────────────────────────────────────────────────────────────

export function OfflineStatusCompact() {
  const status = useSyncStatus()

  if (status.isOnline && status.pendingCount === 0 && !status.isSyncing) {
    return null
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium',
        status.isOnline
          ? status.isSyncing
            ? 'bg-primary/10 text-primary'
            : status.failedCount > 0
              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
      )}
    >
      {status.isOnline ? (
        status.isSyncing ? (
          <>
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Syncing</span>
          </>
        ) : status.failedCount > 0 ? (
          <>
            <AlertTriangle className="w-3 h-3" />
            <span>{status.failedCount} failed</span>
          </>
        ) : (
          <>
            <Upload className="w-3 h-3" />
            <span>{status.pendingCount} pending</span>
          </>
        )
      ) : (
        <>
          <WifiOff className="w-3 h-3" />
          <span>Offline</span>
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

function formatRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)

  if (seconds < 60) return 'Just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

// Alias for backward compatibility
export const OfflineIndicator = OfflineStatusCompact
